import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, RotateCcw, Loader2, ArrowRight, ArrowDown, ArrowUp, Minus } from "lucide-react";

/**
 * RestorePreviewDialog — Mostra o IMPACTO de restaurar um backup ANTES de confirmar.
 * Exibe: contagem de docs por coleção no backup vs. contagem atual no banco + delta.
 * Requer confirmação explícita ("RESTAURAR <empresa>") para evitar cliques acidentais.
 *
 * Endpoint: GET /api/backups/{filename}/preview → { empresa_id, empresa_nome, counts: [...] }
 * fev/2026 code-quality — dá visibilidade total do impacto antes da restauração destrutiva.
 */
export function RestorePreviewDialog({ open, filename, empresa, onClose, onConfirmed }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [restoring, setRestoring] = useState(false);

  useEffect(() => {
    if (!open || !filename) { setPreview(null); setConfirmText(""); return; }
    setLoading(true);
    api.get(`/backups/${encodeURIComponent(filename)}/preview`)
      .then((r) => setPreview(r.data))
      .catch((e) => {
        console.error("Erro ao carregar preview", e);
        toast.error(e?.response?.data?.detail || "Falha ao ler preview");
        onClose();
      })
      .finally(() => setLoading(false));
  }, [open, filename, onClose]);

  const empresaLabel = preview?.empresa_nome || empresa || "esta empresa";
  const expectedText = `RESTAURAR ${empresaLabel}`;
  const canConfirm = confirmText.trim() === expectedText;

  const doRestore = async () => {
    if (!canConfirm) return;
    setRestoring(true);
    try {
      const r = await api.post(`/backups/${encodeURIComponent(filename)}/restore`);
      const c = r.data.restored || {};
      toast.success(`${r.data.empresa_nome} restaurado: ${Object.entries(c).map(([k, v]) => `${k}=${v}`).join(", ")}`);
      onConfirmed?.();
      onClose();
    } catch (e) {
      console.error("Falha ao restaurar", e);
      toast.error(e?.response?.data?.detail || "Falha ao restaurar backup");
    } finally { setRestoring(false); }
  };

  const totalDelta = preview?.counts?.reduce((s, r) => s + Math.abs(r.delta || 0), 0) || 0;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !restoring) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="restore-preview-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-800">
            <AlertTriangle className="w-5 h-5" />
            Preview do Restore
          </DialogTitle>
        </DialogHeader>

        {loading && (
          <div className="text-center py-12 text-muted-foreground">
            <Loader2 className="w-8 h-8 mx-auto mb-2 animate-spin text-amber-600" />
            Analisando conteúdo do backup…
          </div>
        )}

        {!loading && preview && (
          <div className="space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
              <div><b>Empresa alvo:</b> {preview.empresa_nome}</div>
              <div><b>Arquivo:</b> <code className="text-xs">{filename}</code></div>
              {preview.backup_generated_at && (
                <div className="text-xs text-slate-600 mt-1">
                  Gerado em: {new Date(preview.backup_generated_at).toLocaleString("pt-BR")}
                </div>
              )}
            </div>

            <div className="text-sm text-slate-700">
              Ao confirmar, os documentos <b>atuais</b> da empresa serão <b className="text-rose-700">apagados</b> e
              substituídos pelos do backup. Outras empresas <b>não</b> são afetadas.
              Senhas de usuários existentes são preservadas.
            </div>

            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm" data-testid="restore-preview-table">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold">Coleção</th>
                    <th className="px-3 py-2 text-right font-semibold">Atual no banco</th>
                    <th className="px-3 py-2 text-center font-semibold text-xs">→</th>
                    <th className="px-3 py-2 text-right font-semibold text-emerald-700">Após restore</th>
                    <th className="px-3 py-2 text-right font-semibold">Delta</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.counts.map((r) => {
                    const delta = r.delta || 0;
                    const isSame = delta === 0;
                    const isGrow = delta > 0;
                    const Icon = isSame ? Minus : isGrow ? ArrowUp : ArrowDown;
                    const cls = isSame ? "text-slate-400" : isGrow ? "text-emerald-700" : "text-rose-700";
                    return (
                      <tr key={r.coll} className="border-b hover:bg-slate-50/50" data-testid={`preview-row-${r.coll}`}>
                        <td className="px-3 py-2 font-medium">{r.label}</td>
                        <td className="px-3 py-2 text-right font-mono text-slate-700">{r.current.toLocaleString("pt-BR")}</td>
                        <td className="px-3 py-2 text-center"><ArrowRight className="w-3.5 h-3.5 text-slate-400 inline" /></td>
                        <td className="px-3 py-2 text-right font-mono font-bold text-emerald-700">{r.from_backup.toLocaleString("pt-BR")}</td>
                        <td className={`px-3 py-2 text-right font-mono font-semibold ${cls}`}>
                          <Icon className="w-3.5 h-3.5 inline mr-1" />
                          {isSame ? "0" : (delta > 0 ? `+${delta}` : delta).toLocaleString("pt-BR")}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot className="bg-slate-50 border-t">
                  <tr>
                    <td colSpan={4} className="px-3 py-2 text-xs text-right text-muted-foreground">
                      Diferença total (absoluta) entre atual e backup:
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Badge className="bg-amber-100 text-amber-900 border-amber-300" data-testid="preview-total-delta">
                        {totalDelta.toLocaleString("pt-BR")} docs
                      </Badge>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>

            <div className="bg-rose-50 border border-rose-200 rounded-lg p-3">
              <div className="text-xs text-rose-800 font-semibold mb-2">
                Para confirmar, digite exatamente: <code className="bg-white px-1.5 py-0.5 rounded">{expectedText}</code>
              </div>
              <Input
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder={expectedText}
                className="font-mono"
                data-testid="restore-confirm-input"
                autoFocus
              />
            </div>

            <div className="flex gap-2 justify-end pt-2 border-t">
              <Button variant="ghost" onClick={onClose} disabled={restoring} data-testid="btn-restore-cancel">
                Cancelar
              </Button>
              <Button
                onClick={doRestore}
                disabled={!canConfirm || restoring}
                className="bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="btn-restore-confirm"
              >
                {restoring
                  ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Restaurando…</>
                  : <><RotateCcw className="w-4 h-4 mr-2" />Confirmar Restore</>}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
